import { Box, Button } from '@itandi/itandi-bb-ui';
import { toast } from 'react-toastify';
import { useSWRConfig } from 'swr';
import type { DealWithPrices } from '~/models/Deal';
import { ESTIMATION_BY } from '~/models/Deal';
import { canMovingStatus } from '~/models/Staff';
import type { SurveyAnswer } from '~/models/Survey';
import {
  NOW_ACCEPTING,
  IN_CONSTRUCTION,
  IN_ESTIMATION,
  FINISHED,
  SEND_ESTIMATE,
  AWAITING_ACCEPTANCE,
  OWN_IN_ESTIMATION,
  OWN_SEND_ESTIMATE,
  OWN_IN_CONSTRUCTION,
  OWN_AWAITING_ACCEPTANCE,
  OWN_FINISHED
} from '~/models/Status';
import { ConfirmModal } from '~/components/common/parts/modal/ConfirmModal';
import { SurveyModal } from '~/components/common/survey/SurveyModal';
import { useDealSurveys } from '~/hooks/deals/useDealSurveys';
import useItandiAccount from '~/hooks/useItandiAccount';
import { useOpenHandler } from '~/hooks/useOpenHandler';
import {
  forwardStatusValidation,
  type StatusForwardDeps
} from '~/utils/ForwardStatusValidationUtil';
import useLoader from '~/hooks/useLoader';
import { update as updateStatus } from '~/utils/api/DealStatus';
import { isSuccess } from '~/utils/api/api_handler';
import { useDealEstimations } from '~/hooks/deals/useDealEstimations';
import { useDealEstimationHistories } from '~/hooks/deals/useDealEstimationHistories';
import { useDealInspectionReportImages } from '~/hooks/deals/useDealInspectionReportImages';
import { useDealCompletionReportImages } from '~/hooks/deals/useDealCompletionReportImages';
import { useDealEstimationHeaders } from '~/hooks/deals/useDealEstimationHeaders';
import { propertyDealCacheKey } from '~/hooks/usePropertyDeals';

type Props = {
  deal: DealWithPrices;
  propertyDealId: number;
};

export const ForwardStatus: React.FC<Props> = ({
  deal,
  propertyDealId
}: Props) => {
  const { Loader, showLoader, hideLoader } = useLoader();
  const { data: account } = useItandiAccount().currentAccount();

  // FIXME: HookがHookを返すのをやめたい
  const useDealEstimationsApi = useDealEstimations();
  const useDealEstimationHistoriesApi = useDealEstimationHistories();
  const useInspectionImagesApi = useDealInspectionReportImages();
  const useCompletionImagesApi = useDealCompletionReportImages();
  const useEstimationHeadersApi = useDealEstimationHeaders();
  const useSurveysApi = useDealSurveys();

  const { mutate: globalMutate } = useSWRConfig();
  const openSurveyHandler = useOpenHandler();
  const openConfirmHandler = useOpenHandler();

  const getNextStatusId = (): number | null => {
    const withVendorStatuses = [
      IN_ESTIMATION,
      SEND_ESTIMATE,
      IN_CONSTRUCTION,
      AWAITING_ACCEPTANCE,
      FINISHED
    ];
    const ownStatuses = [
      OWN_IN_ESTIMATION,
      OWN_SEND_ESTIMATE,
      OWN_IN_CONSTRUCTION,
      OWN_AWAITING_ACCEPTANCE,
      OWN_FINISHED
    ];

    // 施工会社未選択時は IN_ESTIMATION と OWN_IN_ESTIMATION の判定ができないのでnull返す
    if (deal.dealVendorCompanies.length === 0) return null;
    const isOwn = deal.dealVendorCompanies[0].vendorCompany === null;

    if (deal.statusId === NOW_ACCEPTING) {
      return isOwn ? OWN_IN_ESTIMATION : IN_ESTIMATION;
    }

    const statuses = isOwn ? ownStatuses : withVendorStatuses;
    const currentIndex = statuses.indexOf(deal.statusId);
    return statuses[currentIndex + 1];
  };

  const nextStatusId = getNextStatusId();

  const needRelatedEstimationsFor = (statusId: number): boolean =>
    [IN_ESTIMATION, SEND_ESTIMATE, IN_CONSTRUCTION].includes(statusId);
  const needCompletionImagesFor = (statusId: number): boolean =>
    statusId === IN_CONSTRUCTION;
  const needEstimationHeadersFor = (statusId: number): boolean =>
    [SEND_ESTIMATE, IN_CONSTRUCTION].includes(statusId);

  // 取得が必要なデータの判定
  const needRelatedEstimations = needRelatedEstimationsFor(deal.statusId);
  const needCompletionImages = needCompletionImagesFor(deal.statusId);
  const needEstimationHeaders = needEstimationHeadersFor(deal.statusId);

  // 各種データの取得
  const dealEstimations = useDealEstimationsApi.index(
    needRelatedEstimations ? deal.id : null
  );
  const dealEstimationHistories = useDealEstimationHistoriesApi.index(
    needRelatedEstimations ? deal.id : null
  );
  const inspectionImages = useInspectionImagesApi.index(
    needRelatedEstimations ? deal.id : null
  );
  const completionImages = useCompletionImagesApi.index(
    needCompletionImages ? deal.id : null
  );
  const estimationHeaders = useEstimationHeadersApi.index(
    needEstimationHeaders ? deal.id : null
  );

  // 施工会社によって進行するステータスかどうかの判定
  // FIXME: これもstatusMovingValidationで判定させたい
  // https://itandi-dev.atlassian.net/browse/NSKDEV-2398
  const isVendorActionStatus =
    deal.estimationBy === ESTIMATION_BY.VENDOR
      ? [IN_CONSTRUCTION, NOW_ACCEPTING, IN_ESTIMATION].includes(deal.statusId)
      : deal.statusId === IN_CONSTRUCTION;

  // バリデーション用データ構築
  const statusForwardData: StatusForwardDeps = {
    dealEstimations: dealEstimations.data,
    dealEstimationHistories: dealEstimationHistories.data,
    inspectionImages: inspectionImages.data,
    completionImages: completionImages.data,
    estimationHeaders: estimationHeaders.data
  };

  const validationDataLoaded =
    (!needRelatedEstimations ||
      (dealEstimations.data &&
        dealEstimationHistories.data &&
        inspectionImages.data)) &&
    (!needCompletionImages || completionImages.data) &&
    (!needEstimationHeaders || estimationHeaders.data);
  const canForwardStatus = forwardStatusValidation(
    deal,
    account,
    statusForwardData
  );
  const isForwardButtonEnabled =
    account &&
    validationDataLoaded &&
    canForwardStatus &&
    !isVendorActionStatus &&
    canMovingStatus(account);

  // アンケート関連
  const needSurveys = nextStatusId === FINISHED;
  const { data: surveys } = useSurveysApi.index(needSurveys ? deal.id : null);
  const surveyPresent = needSurveys && !!surveys?.length && !account?.isAdmin;

  const {
    isOpen: openSurveyConfirm,
    handleOpen: handleOpenSurveyConfirm,
    handleClose: handleCloseSurveyConfirm
  } = openSurveyHandler;
  const {
    isOpen: openConfirm,
    handleOpen: handleOpenConfirm,
    handleClose: handleCloseConfirm
  } = openConfirmHandler;

  const refetchStatusRelatedData = (statusId: number): void => {
    if (needRelatedEstimationsFor(statusId)) {
      dealEstimations.mutate();
      dealEstimationHistories.mutate();
      inspectionImages.mutate();
    }
    if (needCompletionImagesFor(statusId)) {
      completionImages.mutate();
    }
    if (needEstimationHeadersFor(statusId)) {
      estimationHeaders.mutate();
    }
  };

  const handleForwardStatus = async (
    surveyAnswers: SurveyAnswer[] | null
  ): Promise<void> => {
    if (nextStatusId === null) return;

    showLoader();
    try {
      const result = await updateStatus(
        deal.id,
        nextStatusId,
        null,
        surveyAnswers
      );
      if (!isSuccess(result)) {
        toast.error(result.message || 'エラーが発生しました。');
        return;
      }
      await globalMutate(propertyDealCacheKey(propertyDealId));
      refetchStatusRelatedData(nextStatusId);
      toast.success('更新しました');
    } finally {
      hideLoader();
    }
  };
  const handleAcceptForward = async (): Promise<void> => {
    await handleForwardStatus(null);
    handleCloseConfirm();
  };
  const handleAcceptForwardWithSurvey = async (
    surveyAnswers: SurveyAnswer[]
  ): Promise<void> => {
    await handleForwardStatus(surveyAnswers);
    handleCloseSurveyConfirm();
  };

  return (
    <>
      <Loader />
      <Box marginTop={5}>
        <Button
          disabled={!isForwardButtonEnabled}
          height="small"
          onClick={surveyPresent ? handleOpenSurveyConfirm : handleOpenConfirm}
          type="button"
        >
          進める
        </Button>
      </Box>
      <ConfirmModal
        actionText="確定"
        confirmType="save"
        handleAccept={handleAcceptForward}
        handleCancel={handleCloseConfirm}
        isOpen={openConfirm}
      >
        ステータスを進めます。
      </ConfirmModal>
      {surveyPresent && (
        <SurveyModal
          handleAccept={handleAcceptForwardWithSurvey}
          handleClose={handleCloseSurveyConfirm}
          isOpen={openSurveyConfirm}
          surveys={surveys ?? []}
        />
      )}
    </>
  );
};
